import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Check, LifeBuoy } from 'lucide-react';
import { useI18n } from '../context/I18nContext';
import { APPS_DATA } from '../data/apps';
import { PLATFORM_LABELS } from '../types';
import { SeoHead } from '../components/common/SeoHead';
import { NotFoundPage } from './NotFoundPage';
import { Badge } from '../components/ui/Badge';
import { StoreBadge } from '../components/ui/StoreBadge';
import { ScreenImage } from '../components/ui/ScreenImage';
import { FeatureList } from '../components/app/FeatureList';
import { ScreenshotGallery } from '../components/app/ScreenshotGallery';
import { ImageLightbox } from '../components/app/ImageLightbox';
import { Accordion } from '../components/ui/Accordion';

export const AppDetailPage: React.FC = () => {
  const { appSlug } = useParams<{ appSlug: string }>();
  const { resolveText, t } = useI18n();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const app = APPS_DATA.find((a) => a.slug === appSlug);

  if (!app) {
    return <NotFoundPage customMessage="요청하신 앱 제품을 찾을 수 없습니다." />;
  }

  const softwareAppSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: app.name,
    operatingSystem: app.platforms.map((p) => PLATFORM_LABELS[p] || p).join(', '),
    applicationCategory: resolveText(app.category),
    description: resolveText(app.description),
    softwareVersion: app.currentVersion,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'KRW',
    },
  };

  const heroShot = app.screenshots?.[0];

  const faqItems =
    app.faqs?.map((f) => ({
      id: f.id,
      title: resolveText(f.question),
      content: resolveText(f.answer),
    })) || [];

  return (
    <div className="flex flex-col">
      <SeoHead
        title={`${app.name} - ${resolveText(app.tagline)}`}
        description={resolveText(app.description)}
        canonicalPath={`/apps/${app.slug}`}
        schemaJson={softwareAppSchema}
      />

      {/* Hero Section */}
      <section className="pt-12 sm:pt-16 pb-16 sm:pb-24 bg-gradient-to-b from-slate-50/80 to-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left: Info & CTAs */}
            <div className="lg:col-span-7 space-y-6">
              {/* Category & Badges */}
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="brand" size="md">
                  {resolveText(app.category)}
                </Badge>
                {app.platforms.map((p) => (
                  <Badge key={p} variant="platform" size="md">
                    {PLATFORM_LABELS[p] || p}
                  </Badge>
                ))}
                <span className="text-xs text-slate-400 font-mono">
                  {app.currentVersion}
                </span>
              </div>

              {/* Icon & App Title */}
              <div className="flex items-center gap-4">
                <img
                  src={app.iconUrl}
                  alt={`${app.name} Icon`}
                  className="w-20 h-20 rounded-2xl object-cover shadow-sm border border-slate-200/80 shrink-0"
                />
                <div>
                  <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 tracking-tight">
                    {app.name}
                  </h1>
                  <p className="text-base sm:text-lg text-slate-700 font-medium mt-1">
                    {resolveText(app.tagline)}
                  </p>
                </div>
              </div>

              {/* Description */}
              <p className="text-base text-slate-600 leading-relaxed max-w-xl">
                {resolveText(app.description)}
              </p>

              {/* Store Download Badges */}
              <div className="pt-2 flex items-center gap-3 flex-wrap">
                {app.appStoreUrl && <StoreBadge platform="ios" url={app.appStoreUrl} size="lg" />}
                {app.googlePlayUrl && <StoreBadge platform="android" url={app.googlePlayUrl} size="lg" />}
              </div>

              {/* Quick link to Support */}
              <div className="pt-4 flex items-center gap-6 text-xs text-slate-500 font-medium">
                <Link
                  to={`/support/${app.slug}`}
                  className="inline-flex items-center gap-1.5 text-primary-700 hover:text-primary-800 transition-colors"
                >
                  <LifeBuoy className="w-4 h-4" />
                  <span>{t.appDetail.supportLinkText}</span>
                </Link>
              </div>
            </div>

            {/* Right: Hero Concept Image (frameless – assets include their own device art) */}
            {heroShot && (
              <div className="lg:col-span-5 flex justify-center">
                <button
                  type="button"
                  onClick={() => setLightboxIndex(0)}
                  className="block w-full max-w-[280px] sm:max-w-[320px] rounded-lg cursor-zoom-in focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                  aria-label={`${resolveText(heroShot.alt)} – ${t.appDetail.lightboxOpen}`}
                >
                  <ScreenImage
                    imageUrl={heroShot.url}
                    altText={resolveText(heroShot.alt)}
                    className="shadow-md"
                    eager
                  />
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Target Audience Section */}
      {app.targetAudience && app.targetAudience.length > 0 && (
        <section className="py-14 sm:py-18 bg-white border-b border-slate-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight text-center mb-8">
              {t.appDetail.targetAudienceTitle}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {app.targetAudience.map((target, idx) => (
                <div
                  key={idx}
                  className="bg-slate-50/80 border border-slate-200/80 rounded-2xl p-5 flex items-start gap-3 shadow-xs"
                >
                  <div className="w-6 h-6 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5" />
                  </div>
                  <p className="text-sm text-slate-700 font-medium leading-relaxed">
                    {resolveText(target)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Key Features Section */}
      <section className="py-16 sm:py-24 bg-slate-50/60 border-b border-slate-200/80">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              {t.appDetail.featuresTitle}
            </h2>
            <p className="mt-2 text-slate-600 text-base">
              매일의 사용 경험을 편안하게 만들기 위해 심혈을 기울여 구현한 핵심 기능입니다.
            </p>
          </div>

          <FeatureList features={app.features} />
        </div>
      </section>

      {/* Screenshot Gallery Section */}
      {app.screenshots && app.screenshots.length > 0 && (
        <section className="py-16 sm:py-24 bg-white border-b border-slate-200/80">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mb-10">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                {t.appDetail.screenshotsTitle}
              </h2>
              <p className="mt-2 text-slate-600 text-base">
                {t.appDetail.screenshotsSubtitle}
              </p>
            </div>

            <ScreenshotGallery screenshots={app.screenshots} onOpen={setLightboxIndex} />
          </div>
        </section>
      )}

      {/* App FAQ Section */}
      {faqItems.length > 0 && (
        <section className="py-16 sm:py-24 bg-white border-b border-slate-200/80">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
                {t.appDetail.faqTitle}
              </h2>
              <p className="mt-2 text-slate-600 text-sm sm:text-base">
                사용 중 가장 자주 문의하시는 질문들을 정리했습니다.
              </p>
            </div>

            <Accordion items={faqItems} />
          </div>
        </section>
      )}

      {app.screenshots && app.screenshots.length > 0 && (
        <ImageLightbox
          images={app.screenshots}
          index={lightboxIndex}
          onChange={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}

      {/* Bottom Download CTA Bar */}
      <section className="py-14 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
            {app.name}와 함께 오늘을 시작해 보세요.
          </h2>
          <p className="text-slate-400 text-sm sm:text-base max-w-lg mx-auto">
            {resolveText(app.tagline)}
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap pt-2">
            {app.appStoreUrl && <StoreBadge platform="ios" url={app.appStoreUrl} size="lg" />}
            {app.googlePlayUrl && <StoreBadge platform="android" url={app.googlePlayUrl} size="lg" />}
          </div>
        </div>
      </section>
    </div>
  );
};
